import React, { createContext, useState } from "react";
import {
    addLineItem,
    applyDiscountCode,
    getCheckout,
    getOrCreateCheckoutId,
    removeDiscountCode,
    updateLineItem,
    applyProxyDiscountV2,
} from "services/storefront/cart";
import axios from "axios";
import getStableCheckout from "helpers/checkout-build-function";
import { checkoutProxyURL } from "utils/constants";
import checkWarrantyProducts from "@root/helpers/checkWarrantyProducts";
import navigate from "@helpers/link-navigate";
import sendLog from "@root/utils/new-relic-logger";
import NEW_RELIC_LEVELS from "@root/constants/NEW_RELIC_LEVELS";

const defaultPrice = { currencyCode: "USD", amount: 0 };

const initialState = {
    items: [],
    error: "",
    checkoutUrl: "",
    discountCode: "",
    totalPrice: defaultPrice,
    subtotalPrice: defaultPrice,
    discountPrice: defaultPrice,
    createdAt: "",
    updatedAt: "",
    hasDiscount: "",
    isLoading: true,
};

const getAppliedDiscountCodeErrors = (discountCode) => {
    switch (discountCode) {
        case "DISCOUNT_NOT_FOUND":
            return "Unable to find a valid discount matching the code entered";
        case "ALREADY_EXISTS":
            return "Discount code has already been applied to the cart";
        case "EXPIRED":
            return "Discount code has expired";
        case "HIGHER_VALUE_DISCOUNT_APPLIED":
            return "Discount couldn't be used with your existing discounts.";
        default:
            return "Unable to find a valid discount matching the code entered";
    }
};

export const CartContext = createContext(null);

const useCart = () => {
    const [cart, setCart] = useState(initialState);

    const editCartItem = (item) => {
        const productHandleAttr = item.node.attributes.find(attr => attr.key === "_handle")?.value;
        const productPath = productHandleAttr ? `/products/${productHandleAttr}` : `/products/${item.node.merchandise.product.handle}`;

        let measurements = item.node.attributes.reduce((acc, item) => {
            if (item.key.startsWith("C")) {
                acc[item.key] = item.value;
            }
            return acc;
        }, {});

        let customAttributes = item.node.attributes.reduce((acc, item) => {
            let value = item.value;

            if (item.key === 'c_Print Side' || item.key === 'customization_variant_ids') {
                value = item.value.split(",");
            }

            acc[item.key] = value;

            return acc;
        }, {});


        const editData = {
            customAttributes: customAttributes,
            configuration: {
                product: item.node.merchandise.product.id.split('/').pop(),
                variant: item.node.merchandise.id.split('/').pop(),
                quantity: item.node.quantity,
                customization_variant_ids: item.node.attributes
                    .find(attr => attr.key === "customization_variant_ids")?.value.split(","),

                ...measurements
            },
            isEditing: true,
            item,
        }

        navigate(productPath, { state: { editData } });
    };

    const removeCartItem = async (itemId) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const response = await updateLineItem(checkoutId, [{ id: itemId, quantity: 0 }]);

            const updatedCartItems = cart.items.filter((cartItem) => cartItem.node.id !== itemId);

            setCart((prev) => ({
                ...prev,
                items: updatedCartItems,
            }));
        } catch (error) {
            console.error("Error removing cart item:", error);
        }
    };

    // Should be used on any function that changes the Checkout Object
    const updateCart = async (checkout) => {
        if (!Object.keys(checkout || {}).length) return;
        const lineItems = checkout?.lines;

        // Cart State
        const createdAt = new Date(checkout?.createdAt);
        const updatedAt = new Date(checkout?.updatedAt);
        const items = lineItems?.edges?.length ? lineItems.edges : [];


        const calculatedPrice = items.reduce((acc, item) => {
            const itemTotalPrice = item?.node?.attributes.length > 0 && item?.node?.attributes.find((attr) => attr.key === "__basePrice") ? +item.node.attributes.filter((attr) => attr.key === "__basePrice")[0].value * item.node.quantity : +item.node.merchandise.price.amount * item.node.quantity;
            return acc + itemTotalPrice;
        }, 0);


        const newCart = await getStableCheckout({ items });

        let appliedDiscount = await applyProxyDiscountV2({ ...newCart, discountCode: checkout.discountCodes.length > 0 ? checkout.discountCodes[0].code : "" });

        if(!appliedDiscount || appliedDiscount?.error === true) {
            appliedDiscount = { total_discounts: 0 };
        }

        const subtotalPrice = { currencyCode: checkout?.cost.subtotalAmount.currencyCode, amount: calculatedPrice };
        const hasDiscount = Boolean(checkout.discountCodes.length);

        const checkoutUrl = checkout?.checkoutUrl;
        const totalPrice = { currencyCode: checkout?.cost.totalAmount.currencyCode, amount: appliedDiscount?.total_discounts !== 0 ? calculatedPrice - +appliedDiscount.total_discounts : calculatedPrice };
        const discountCode = checkout?.discountCodes.length > 0 ? checkout.discountCodes[0].code : "";
        const discountPrice = { currencyCode: subtotalPrice.currencyCode, amount: appliedDiscount?.total_discounts !== 0 ? +appliedDiscount.total_discounts : subtotalPrice };

        // console.log("track cart", {
        //     items,
        //     checkoutUrl,
        //     discountCode,
        //     totalPrice,
        //     subtotalPrice,
        //     discountPrice,
        //     createdAt,
        //     updatedAt,
        //     hasDiscount,
        //     error: false,
        //     isLoading: false,
        // });

        setCart(() => ({
            items,
            checkoutUrl,
            discountCode,
            totalPrice,
            subtotalPrice,
            discountPrice,
            createdAt,
            updatedAt,
            hasDiscount,
            error: false,
            isLoading: false,
        }));
    };

    // Is called only when initially landing on the page
    const fetchCart = async () => {
        try {

            let checkoutId = await getOrCreateCheckoutId();
            let checkout = await getCheckout(checkoutId);

            // Create new checkout if the old checkout is completed or doesn't exist anymore
            if (checkout?.data?.cart === null) { // || checkout?.data?.cart?.completedAt !== null // ToDo: Check how we can know if the checkout is completed
                checkoutId = await getOrCreateCheckoutId(false, []);
                checkout = await getCheckout(checkoutId);
            }

            const lastUpdatedAt = new Date(checkout.data.cart.updatedAt).getTime();

            // Should only be called if the cart has been updated or the first time landing in the website
            if (!cart.updatedAt || !(cart.updatedAt.getTime() === lastUpdatedAt)) await updateCart(checkout.data.cart);

        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const addCartItem = async (items) => {
        let checkoutId = "";
        let lineItems = [];

        try {
            checkoutId = await getOrCreateCheckoutId();
            lineItems = items.reduce((arr, item) => [...arr, {
                merchandiseId: item.variantId,
                quantity: item?.quantity,
                attributes: item?.customAttributes || [],
            }], []);
            const updatedCheckout = await addLineItem(checkoutId, lineItems);

            if (updatedCheckout?.data?.cartLinesAdd?.userErrors?.length > 0) {
                const errorMessage = updatedCheckout?.data?.cartLinesAdd?.userErrors[0]?.message || "Unknown error";
                sendLog(`[Add To Cart] Error: ${errorMessage} CheckoutID: ${checkoutId} Line Items: ${JSON.stringify(lineItems)} Response: ${updatedCheckout?.data}`, NEW_RELIC_LEVELS.FATAL);
            }

            await updateCart(updatedCheckout.data.cartLinesAdd.cart, "some");
        } catch (err) {
            sendLog(`[Add To Cart 2] Error: ${err} CheckoutID: ${checkoutId} Line Items: ${JSON.stringify(lineItems)}`, NEW_RELIC_LEVELS.FATAL);

            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const updateCartItem = async (variantId, quantity) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const lineItem = [{
                // eslint-disable-next-line id-length
                id: variantId,
                quantity,
            }];

            const updatedCheckout = await updateLineItem(checkoutId, lineItem);

            await updateCart(updatedCheckout.data.cartLinesUpdate.cart);
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const applyDiscount = async (discountCode, onSuccessfullyAppliedCode) => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const updatedCheckout = await applyDiscountCode(checkoutId, discountCode);
            const discountMessage = Boolean(updatedCheckout.data.cartDiscountCodesUpdate.cart?.discountCodes?.length);

            if (!discountMessage || updatedCheckout.data.cartDiscountCodesUpdate?.userErrors?.length > 0 || updatedCheckout.data.cartDiscountCodesUpdate?.cart?.discountCodes?.length === 0) {
                setCart((prev) => ({ ...prev, hasDiscount: getAppliedDiscountCodeErrors(updatedCheckout.data.cartDiscountCodesUpdate?.userErrors[0]?.code) }));
            } else {
                await updateCart(updatedCheckout.data.cartDiscountCodesUpdate.cart);

                if (onSuccessfullyAppliedCode) onSuccessfullyAppliedCode(updatedCheckout.data.cartDiscountCodesUpdate);
            }
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
            console.log(err);
        }
    };

    const removeDiscount = async () => {
        try {
            const checkoutId = await getOrCreateCheckoutId();
            const updatedCheckout = await applyDiscountCode(checkoutId, "");
            await updateCart(updatedCheckout.data.cartDiscountCodesUpdate.cart);
        } catch (err) {
            setCart(async (prevState) => ({ ...prevState, error: err }));
        }
    };

    const checkoutRedirect = async (inputRefs) => {
        const hasPatioProducts = cart.items.some((item) => item.node.attributes.some((attr) => attr.key === "_customProduct"));
        const hasWarrantyProducts = checkWarrantyProducts(cart.items);

        if (hasPatioProducts || hasWarrantyProducts) {
            const checkoutObject = await getStableCheckout(cart, inputRefs);
            const proxyCheckout = await axios.post(checkoutProxyURL, checkoutObject)
                .catch((error) => {
                    // eslint-disable-next-line no-console
                    console.log(error);
                });
            return proxyCheckout.data;
        }


        const url = new URL(cart.checkoutUrl);
        url.hostname = new URL(process.env.GATSBY_STORE_URL).hostname;

        return { checkout_link: url.toString() };
    };

    return {
        cart, updateCartItem, addCartItem, fetchCart, applyDiscount, removeDiscount, checkoutRedirect, editCartItem, removeCartItem,
    };
};

const CartProvider = ({ children }) => {
    const cart = useCart();
    return (
        <CartContext.Provider value={cart}>
            {children}
        </CartContext.Provider>
    );
};

export default CartProvider;
